import React, { useState, useEffect } from "react";
import { Select, Table, Input, Button, message, Modal, Form } from "antd";
import axios from "./Services/Api";
import {baseURL} from './api'
const { Option } = Select;

const VendorInventory = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [stockData, setStockData] = useState({}); // Stores current stock data
  const [updatedStock, setUpdatedStock] = useState({}); // Stores updated stock values
  const [editableRows, setEditableRows] = useState({}); // Tracks which rows are being edited
  const [isVendorModalVisible, setIsVendorModalVisible] = useState(false);
  const [form] = Form.useForm();

  const currentVendorId = localStorage.getItem("vendorId");

  // Fetch Restaurants based on Vendor
  const fetchRestaurants = async () => {
    try {
      const { data } = await axios.get("/getRestaurants");

      const vendorRestaurants = data.filter(
        (r) => r.vendorId === currentVendorId || r.default === true
      );

      setRestaurants(vendorRestaurants);

      if (vendorRestaurants.length > 0) {
        const defaultSelection = vendorRestaurants[0]._id;
        setSelectedRestaurant(defaultSelection);
        fetchMenuItems(defaultSelection);
      }
    } catch (error) {
      message.error("Failed to fetch restaurants.");
    }
  };

  // Fetch Menu Items and Vendor Stock Data
  const fetchMenuItems = async (restaurantId) => {
    try {
      // Fetch Menu Items
      const { data: menuItemsData } = await axios.get(
        `/getMenuItems?restaurantId=${restaurantId}`
      );
      setMenuItems(menuItemsData);

      // If no menu items, display a message
      if (menuItemsData.length === 0) {
        message.warning("No menu items added yet for this restaurant.");
      }

      // Fetch Vendor Stock Data
      const { data: vendorStockResponse } = await axios.get(
        `/getVendorStock?restaurantId=${restaurantId}&vendorId=${currentVendorId}`
      );
      const stockMap = {};

      // Map stock data with menu item IDs for easier access
      vendorStockResponse.stock.forEach((stockItem) => {
        stockMap[stockItem.menuItemId._id] = stockItem.quantity;
      });

      setStockData(stockMap);
      setUpdatedStock({}); // Clear updated stock when fetching new data
      setEditableRows({}); // Reset editable rows
    } catch (error) {
      message.error("Failed to fetch vendor stock or menu items.");
    }
  };

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const handleRestaurantChange = (value) => {
    setSelectedRestaurant(value);
    fetchMenuItems(value); // Fetch items when restaurant changes
  };

  const handleStockChange = (value, itemId) => {
    setUpdatedStock((prev) => ({
      ...prev,
      [itemId]: parseInt(value, 10) || 0, // Update stock when changed
    }));
  };

  const handleUpdateClick = (itemId) => {
    setEditableRows((prev) => ({
      ...prev,
      [itemId]: true, // Mark the row as editable when the update button is clicked
    }));
  };

  const handleSaveStock = async () => {
    try {
      if (!selectedRestaurant) {
        message.error("Restaurant selection is missing.");
        return;
      }

      // Prepare stock update data by comparing original and updated values
      const stockUpdates = Object.entries(updatedStock)
        .filter(([itemId, inStock]) => {
          const original = stockData[itemId] ?? 0;
          return Number(inStock) !== Number(original);
        })
        .map(([itemId, inStock]) => ({
          itemId,
          inStock: Number(inStock),
        }));

      if (stockUpdates.length === 0) {
        message.warning("No changes to save.");
        return;
      }

      // Call API to update stock data
      await axios.post(`/updateVendorStock`, {
        restaurantId: selectedRestaurant,
        stockUpdates,
      });

      message.success("Vendor stock updated successfully!");
      fetchMenuItems(selectedRestaurant); // Refetch updated menu items and stock
    } catch (error) {
      message.error("Failed to update vendor stock.");
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      {/* Restaurant Selection */}
      <Select
        placeholder="Select a restaurant"
        style={{ width: 300, marginBottom: "20px" }}
        value={selectedRestaurant}
        onChange={handleRestaurantChange}
      >
        {restaurants.map((restaurant) => (
          <Option key={restaurant._id} value={restaurant._id}>
            {restaurant.name}
          </Option>
        ))}
      </Select>

      {/* Menu Items and Stock Table */}
      {menuItems.length > 0 ? (
        <>
          <Table
            dataSource={menuItems.map((item) => ({
              ...item,
              key: item._id,
            }))}
            columns={[
              {
                title: "Image",
                dataIndex: "imgPath",
                key: "imgPath",
                render: (imgPath) => (
                  <img
                    src={`${baseURL}/${imgPath}`}
                    alt="Menu Item"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "cover",
                    }}
                  />
                ),
              },
              { title: "Item", dataIndex: "name", key: "name" },
              { title: "Price", dataIndex: "price", key: "price" },
              {
                title: "Current Stock (Kg)",
                key: "currentStock",
                render: (_, record) => (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Input
                      type="number"
                      min="0"
                      value={updatedStock[record._id] ?? stockData[record._id] ?? 0}
                      onChange={(e) => handleStockChange(e.target.value, record._id)}
                      style={{ width: "80px" }}
                      disabled={!editableRows[record._id]}
                    />
                  </div>
                ),
              },
            ]}
            pagination={false}
          />

          {/* Save Stock Button */}
          <Button
            type="primary"
            onClick={handleSaveStock}
            style={{ marginTop: "20px" }}
            disabled={Object.keys(updatedStock).length === 0}
          >
            Save Stock
          </Button>
        </>
      ) : (
        <p>No menu items added yet for this restaurant.</p> // Added message for no menu items
      )}
    </div>
  );
};

export default VendorInventory;
